import PropTypes from 'prop-types';
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DownloadRounded, History, Psychology } from '@mui/icons-material';
import {
  ACTION_META,
  LABEL_META,
  WATCH_CONVICTION_META,
  WATCH_ENTRY_META,
  WATCH_PRIORITY_META,
  WATCH_STRENGTH_META,
  WATCH_SUMMARY_ICON,
  WATCH_SUMMARY_META,
  watchlistTdSx,
  watchlistThSx,
} from './shared';

const WatchlistTable = ({ locale, onOpen, rows, t }) => (
  <TableContainer>
    <Table size="small" sx={{ minWidth: 860 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...watchlistThSx, width: 42 }}>{t('behavior.table.index')}</TableCell>
          <TableCell sx={{ ...watchlistThSx, width: 190 }}>{t('behavior.table.ticker')}</TableCell>
          <TableCell sx={watchlistThSx}>{t('behavior.table.behavior')}</TableCell>
          <TableCell sx={watchlistThSx}>{t('behavior.table.action')}</TableCell>
          <TableCell sx={watchlistThSx}>{t('behavior.watchlist.priority')}</TableCell>
          <TableCell sx={watchlistThSx}>{t('behavior.watchlist.conviction')}</TableCell>
          <TableCell sx={watchlistThSx}>{t('behavior.watchlist.strength')}</TableCell>
          <TableCell sx={watchlistThSx}>{t('behavior.watchlist.entrySpeed')}</TableCell>
          <TableCell sx={{ ...watchlistThSx, minWidth: 190 }}>{t('behavior.watchlist.whyBuyNow')}</TableCell>
          <TableCell sx={watchlistThSx}>{t('behavior.table.sector')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((candidate, index) => {
          const labelMeta = LABEL_META[candidate.behavior_label] || LABEL_META.NEUTRAL;
          const actionMeta = ACTION_META[candidate.tactical_action] || ACTION_META.IGNORE;
          const display = candidate.watch_display || {};
          const priorityMeta = WATCH_PRIORITY_META[display.priority_band] || WATCH_PRIORITY_META.low;
          const convictionMeta = WATCH_CONVICTION_META[display.conviction_band] || WATCH_CONVICTION_META.low;
          const strengthMeta = WATCH_STRENGTH_META[display.strength_band] || WATCH_STRENGTH_META.rest;
          const entryMeta = WATCH_ENTRY_META[display.entry_band] || WATCH_ENTRY_META.flat;
          const reasons = (display.signal_codes || [])
            .map((code) => ({ code, label: t(`behavior.watchlist.reasons.${code}`) }))
            .filter((item) => item.label)
            .slice(0, 2);

          return (
            <TableRow
              key={candidate.ticker}
              hover
              onClick={() => onOpen(candidate.ticker)}
              sx={{
                cursor: 'pointer',
                '&:nth-of-type(odd)': { bgcolor: alpha(actionMeta.color, 0.02) },
                '&:hover': { bgcolor: alpha(actionMeta.color, 0.06) },
              }}
            >
              <TableCell sx={{ ...watchlistTdSx, color: '#94a3b8', fontWeight: 700, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                {index + 1}
              </TableCell>
              <TableCell sx={watchlistTdSx}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#2563eb', lineHeight: 1.15, mb: 0.15 }}>
                    {candidate.ticker}
                  </Typography>
                  {candidate.company_name ? (
                    <Typography sx={{ fontSize: '0.66rem', color: '#64748b', lineHeight: 1.25, maxWidth: 250 }}>
                      {candidate.company_name}
                    </Typography>
                  ) : null}
                </Box>
              </TableCell>
              <TableCell sx={watchlistTdSx}>
                <Chip
                  label={t(`behavior.labels.${candidate.behavior_label || 'NEUTRAL'}.short`)}
                  size="small"
                  sx={{ height: 22, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha(labelMeta.color, 0.1), color: labelMeta.color, border: `1px solid ${alpha(labelMeta.color, 0.12)}` }}
                />
              </TableCell>
              <TableCell sx={watchlistTdSx}>
                <Chip
                  icon={actionMeta.icon ? <Box sx={{ display: 'flex', alignItems: 'center', color: `${actionMeta.color} !important` }}>{actionMeta.icon}</Box> : undefined}
                  label={t(`behavior.actions.${candidate.tactical_action || 'IGNORE'}`)}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    bgcolor: alpha(actionMeta.color, 0.06),
                    color: actionMeta.color,
                    border: `1px solid ${alpha(actionMeta.color, 0.2)}`,
                    '& .MuiChip-icon': { ml: 0.6 },
                  }}
                />
              </TableCell>
              <TableCell sx={watchlistTdSx}>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: priorityMeta.color, lineHeight: 1.2 }}>
                  {t(priorityMeta.labelKey)}
                </Typography>
              </TableCell>
              <TableCell sx={watchlistTdSx}>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: convictionMeta.color, lineHeight: 1.2 }}>
                  {t(convictionMeta.labelKey)}
                </Typography>
              </TableCell>
              <TableCell sx={watchlistTdSx}>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: strengthMeta.color, lineHeight: 1.2 }}>
                  {t(strengthMeta.labelKey)}
                </Typography>
              </TableCell>
              <TableCell sx={watchlistTdSx}>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: entryMeta.color, lineHeight: 1.2 }}>
                  {t(entryMeta.labelKey)}
                </Typography>
              </TableCell>
              <TableCell sx={watchlistTdSx}>
                {reasons.length ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, maxWidth: 240 }}>
                    {reasons.map((reason, reasonIndex) => (
                      <Box key={`${candidate.ticker}-${reason.code}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6, minWidth: 0 }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: reasonIndex === 0 ? '#2563eb' : '#cbd5e1', mt: 0.55, flexShrink: 0 }} />
                        <Typography
                          sx={{
                            fontSize: reasonIndex === 0 ? '0.67rem' : '0.63rem',
                            fontWeight: reasonIndex === 0 ? 700 : 600,
                            color: reasonIndex === 0 ? '#334155' : '#64748b',
                            lineHeight: 1.25,
                          }}
                        >
                          {reason.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '0.68rem', color: '#cbd5e1', lineHeight: 1.2 }}>—</Typography>
                )}
              </TableCell>
              <TableCell sx={watchlistTdSx}>
                <Typography sx={{ fontSize: '0.74rem', color: '#64748b', whiteSpace: 'nowrap', lineHeight: 1.25 }}>
                  {candidate.sector?.[locale] || candidate.sector?.vi || candidate.sector?.en || candidate.sector?.code || '—'}
                </Typography>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

WatchlistTable.propTypes = {
  locale: PropTypes.string.isRequired,
  onOpen: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({
    behavior_label: PropTypes.string,
    company_name: PropTypes.string,
    sector: PropTypes.shape({
      code: PropTypes.string,
      en: PropTypes.string,
      vi: PropTypes.string,
    }),
    tactical_action: PropTypes.string,
    ticker: PropTypes.string,
    watch_display: PropTypes.shape({
      conviction_band: PropTypes.string,
      entry_band: PropTypes.string,
      priority_band: PropTypes.string,
      signal_codes: PropTypes.arrayOf(PropTypes.string),
      strength_band: PropTypes.string,
    }),
  })).isRequired,
  t: PropTypes.func.isRequired,
};

const WatchlistSection = ({
  exportingWatchlistImage,
  locale,
  onExportWatchlistImage,
  onOpen,
  t,
  watchlist,
  watchlistExportRef,
  watchlistSummary,
}) => (
  <Box ref={watchlistExportRef} sx={{ bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #e2e8f0', mb: 3, overflow: 'hidden' }}>
    <Box sx={{ px: 2.5, pt: 2, pb: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Psychology sx={{ fontSize: 14, color: '#64748b' }} />
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>{t('behavior.watchlist.title')}</Typography>
          <Chip label={watchlist.length} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha('#3b82f6', 0.08), color: '#3b82f6' }} />
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={exportingWatchlistImage ? <CircularProgress size={14} color="inherit" /> : <DownloadRounded sx={{ fontSize: 16 }} />}
          onClick={onExportWatchlistImage}
          disabled={exportingWatchlistImage || watchlist.length === 0}
          data-html2canvas-ignore="true"
          sx={{
            minWidth: 0,
            height: 24,
            px: 1,
            borderRadius: 1,
            textTransform: 'none',
            fontSize: '0.68rem',
            fontWeight: 700,
            borderColor: '#cbd5e1',
            color: '#475569',
            bgcolor: '#fff',
            '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
          }}
        >
          {exportingWatchlistImage ? t('behavior.watchlist.exportingImage') : t('behavior.watchlist.exportImage')}
        </Button>
      </Box>
      <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mb: 1.5 }}>
        {t('behavior.watchlist.subtitle')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        {['high', 'mid', 'low'].filter((band) => (watchlistSummary[band] || 0) > 0).map((band) => {
          const meta = WATCH_SUMMARY_META[band];
          const SummaryIcon = WATCH_SUMMARY_ICON[band] || History;

          return (
            <Box key={band} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SummaryIcon sx={{ fontSize: 13, color: meta.color }} />
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: meta.color }}>
                {t(meta.labelKey, { count: watchlistSummary[band] })}
              </Typography>
              <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>- {t(meta.descKey)}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>

    {watchlist.length ? (
      <WatchlistTable locale={locale} onOpen={onOpen} rows={watchlist} t={t} />
    ) : (
      <Box sx={{ py: 3, textAlign: 'center', color: '#94a3b8', fontSize: '0.76rem' }}>
        {t('behavior.watchlist.empty')}
      </Box>
    )}
  </Box>
);

WatchlistSection.propTypes = {
  exportingWatchlistImage: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  onExportWatchlistImage: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  watchlist: PropTypes.arrayOf(PropTypes.object).isRequired,
  watchlistExportRef: PropTypes.shape({ current: PropTypes.any }),
  watchlistSummary: PropTypes.shape({
    high: PropTypes.number,
    low: PropTypes.number,
    mid: PropTypes.number,
  }).isRequired,
};

WatchlistSection.defaultProps = {
  watchlistExportRef: null,
};

export default WatchlistSection;
